import apiClient from './axios.config';
import { parseBlobError } from './utils/error-handler';
import {
  AccountDto,
  PaginatedAccountDto,
  CreateAccountDto,
  ChangePasswordAccountDto,
  AccountForgotPasswordDto,
  PaginatedCharacterDto,
  PaginatedInventoryItemDto,
  PaginatedWarehouseItemDto,
  MoveItemToWarehouseDto,
  MoveItemFromWarehouseDto,
  ExchangeMoneyRequestDto,
  GameServerDto,
  WorldDataDto,
} from '../types';

export const l2Api = {
  /**
   * Get list of available game servers
   */
  getServers: async (): Promise<GameServerDto[]> => {
    const response = await apiClient.get<GameServerDto[]>('/l2/servers');
    return response.data;
  },

  /**
   * Get all L2 accounts for current user with pagination
   */
  getAccounts: async (page: number = 0, size: number = 20): Promise<PaginatedAccountDto> => {
    const response = await apiClient.get<PaginatedAccountDto>('/l2/accounts', {
      params: { page, size },
    });
    return response.data;
  },

  /**
   * Create new L2 account and download credentials as .txt file
   */
  createAccount: async (data: CreateAccountDto): Promise<{ blob: Blob; filename: string }> => {
    try {
      const response = await apiClient.post('/l2/accounts', data, {
        responseType: 'blob',
      });

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${data.login}_credentials.txt`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return { blob: response.data, filename };
    } catch (err) {
      await parseBlobError(err);
      throw err;
    }
  },

  /**
   * Change L2 account password
   */
  changeAccountPassword: async (data: ChangePasswordAccountDto): Promise<void> => {
    await apiClient.patch('/l2/accounts/password', data);
  },

  /**
   * Reset L2 account password with token
   */
  resetAccountPassword: async (data: AccountForgotPasswordDto): Promise<void> => {
    await apiClient.patch('/l2/accounts/password/recovery', data);
  },

  /**
   * Request L2 account password reset email
   */
  requestAccountPasswordReset: async (accountLogin: string): Promise<void> => {
    await apiClient.post(`/l2/accounts/${accountLogin}/password/request`);
  },

  /**
   * Get all characters for current user with pagination and search
   */
  getAllCharacters: async (
    page: number = 0,
    size: number = 20,
    search?: string
  ): Promise<PaginatedCharacterDto> => {
    const params: Record<string, any> = { page, size };
    if (search?.trim()) {
      params.search = search.trim();
    }
    const response = await apiClient.get<PaginatedCharacterDto>('/l2/characters', { params });
    return response.data;
  },

  /**
   * Teleport character to capital
   */
  teleportToCapital: async (charName: string): Promise<AccountDto> => {
    const response = await apiClient.post<AccountDto>(
      `/l2/characters/teleport/${charName}/capital`
    );
    return response.data;
  },

  /**
   * Get character inventory items
   */
  getCharacterInventory: async (
    characterName: string,
    page: number = 0,
    size: number = 50
  ): Promise<PaginatedInventoryItemDto> => {
    const response = await apiClient.get<PaginatedInventoryItemDto>(
      `/l2/items/${characterName}`,
      { params: { page, size } }
    );
    return response.data;
  },

  /**
   * Move items from character inventory to warehouse
   */
  moveToWarehouse: async (
    characterName: string,
    data: MoveItemToWarehouseDto
  ): Promise<void> => {
    await apiClient.post(
      `/l2/items/${characterName}/warehouse`,
      data
    );
  },

  /**
   * Get warehouse items for current user
   */
  getWarehouse: async (page: number = 0, size: number = 50): Promise<PaginatedWarehouseItemDto> => {
    const response = await apiClient.get<PaginatedWarehouseItemDto>('/warehouse', {
      params: { page, size },
    });
    return response.data;
  },

  /**
   * Exchange money for in-game currency (to any character by name)
   */
  exchangeMoney: async (
    characterName: string,
    data: ExchangeMoneyRequestDto
  ): Promise<void> => {
    await apiClient.post(`/money/characters/${characterName}/exchange`, data);
  },

  /**
   * Move items from warehouse to character inventory
   */
  getWorldData: async (): Promise<WorldDataDto> => {
    const response = await apiClient.get<WorldDataDto>('/l2/world');
    return response.data;
  },

  moveToInventory: async (
    accountLogin: string,
    characterName: string,
    data: MoveItemFromWarehouseDto
  ): Promise<void> => {
    await apiClient.post(
      `/warehouse/${accountLogin}/characters/${characterName}/inventory`,
      data
    );
  },
};
